// src/components/LayerPanel/SetsFlyout.jsx
//
// Layer Sets, as a flyout off the panel header's "Sets" button. Sets are a
// load/save mechanism, not a browsable scope — so they get a flyout, not a
// tab. The body is ported from the old LayerSetsPanel; the wrapper turns it
// into an absolutely-positioned dropdown, and applying a set closes it.
//
// Positioned by the caller: render this inside a `relative` element; it pins
// itself to `top-full right-0`.

import { useState } from "react";
import { BookMarked, Trash2, X, Plus } from "lucide-react";
import { useLayerSetsStore } from "../../stores/useLayerSetsStore";

function cx(...c) { return c.filter(Boolean).join(" "); }

export default function SetsFlyout({ onClose }) {
  const sets = useLayerSetsStore((s) => s.sets);
  const loading = useLayerSetsStore((s) => s.loading);
  const saveSet = useLayerSetsStore((s) => s.saveSet);
  const applySet = useLayerSetsStore((s) => s.applySet);
  const deleteSet = useLayerSetsStore((s) => s.deleteSet);
  const setDefault = useLayerSetsStore((s) => s.setAsDefault);
  const renameSet = useLayerSetsStore((s) => s.renameSet);
  const saveAsDefault = useLayerSetsStore((s) => s.saveAsDefault);

  const [naming, setNaming] = useState(false);
  const [newName, setNewName] = useState("");
  const [asDefault, setAsDefault] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!newName.trim()) return;
    setSaving(true);
    await saveSet(newName.trim(), asDefault);
    setNewName("");
    setNaming(false);
    setAsDefault(false);
    setSaving(false);
  }

  async function handleSaveDefault() {
    setSaving(true);
    await saveAsDefault();
    setSaving(false);
  }

  function handleApply(setObj) {
    applySet(setObj);
    onClose?.();
  }

  return (
    <div className="absolute right-0 top-full mt-1 z-50 w-64 max-h-[60vh] overflow-y-auto rounded-md border border-slate-700 bg-bg-softer shadow-xl shadow-black/50">
      <div className="px-3 py-2 border-b border-slate-700/60">
        <span className="text-[10px] font-semibold font-mono uppercase tracking-[0.14em] text-gray-400">
          Layer Sets
        </span>
      </div>

      {loading ? (
        <div className="px-3 py-3 text-[10px] text-fg-secondary animate-pulse">
          Loading sets…
        </div>
      ) : (
        <div className="px-2 py-2 space-y-1">
          <button
            onClick={handleSaveDefault}
            disabled={saving}
            className="flex items-center gap-1.5 w-full px-2 py-[6px] rounded border border-slate-700
              text-[10px] text-fg-secondary hover:text-gray-300 hover:border-slate-500 transition mb-2"
          >
            <BookMarked size={11} className="flex-shrink-0" />
            {saving ? "Saving…" : "Update project default"}
          </button>

          {sets.length === 0 && !naming && (
            <div className="text-[10px] text-fg-secondary px-1 pb-1">
              Save your current layers as a named set. Mark one as default to
              restore it automatically when you open this project.
            </div>
          )}

          {sets.map((s) => (
            <div
              key={s.id}
              className={cx(
                "group flex items-center gap-2 px-2 py-[6px] rounded border transition",
                s.is_default
                  ? "bg-accent-info/[0.08] border-accent-info/30"
                  : "bg-bg-soft border-slate-800 hover:border-slate-700"
              )}
            >
              <BookMarked
                size={11}
                className={cx("flex-shrink-0", s.is_default ? "text-accent-info" : "text-fg-secondary")}
              />

              {renamingId === s.id ? (
                <input
                  autoFocus
                  value={renameVal}
                  onChange={(e) => setRenameVal(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === "Enter") { await renameSet(s.id, renameVal); setRenamingId(null); }
                    if (e.key === "Escape") setRenamingId(null);
                  }}
                  onBlur={() => setRenamingId(null)}
                  className="flex-1 bg-bg border border-slate-700 rounded px-1 py-px
                    text-[11px] text-gray-200 focus:outline-none focus:border-accent-info/60"
                />
              ) : (
                <button
                  onClick={() => handleApply(s)}
                  onDoubleClick={() => { setRenamingId(s.id); setRenameVal(s.name); }}
                  className="flex-1 text-left text-[11px] text-gray-300 hover:text-gray-100 truncate"
                  title="Click to apply · Double-click to rename"
                >
                  {s.name}
                </button>
              )}

              {s.is_default && (
                <span className="text-[8px] font-mono px-1 py-px rounded
                  bg-accent-info/15 text-accent-info border border-accent-info/20 flex-shrink-0">
                  default
                </span>
              )}

              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
                <button
                  onClick={() => setDefault(s.id, !s.is_default)}
                  title={s.is_default ? "Remove as default" : "Set as default"}
                  className="p-0.5 rounded hover:bg-slate-700 text-fg-secondary hover:text-accent-action"
                >
                  <BookMarked size={10} />
                </button>
                <button
                  onClick={() => deleteSet(s.id)}
                  className="p-0.5 rounded hover:bg-slate-700 text-fg-secondary hover:text-red-400"
                >
                  <Trash2 size={10} />
                </button>
              </div>
            </div>
          ))}

          {naming ? (
            <div className="space-y-1 mt-1">
              <div className="flex items-center gap-1">
                <input
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSave();
                    if (e.key === "Escape") { setNaming(false); setNewName(""); setAsDefault(false); }
                  }}
                  placeholder="Set name…"
                  className="flex-1 bg-bg border border-slate-700 rounded px-2 py-1 text-[11px]
                    text-gray-200 placeholder-gray-600 focus:outline-none focus:border-accent-action/60"
                />
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-2 py-1 rounded bg-accent-action/20 border border-accent-action/40
                    text-accent-action text-[10px] hover:bg-accent-action/30 transition"
                >
                  {saving ? "…" : "Save"}
                </button>
                <button
                  onClick={() => { setNaming(false); setNewName(""); setAsDefault(false); }}
                  className="p-1 rounded hover:bg-slate-700 text-fg-secondary"
                >
                  <X size={12} />
                </button>
              </div>
              <label className="flex items-center gap-2 text-[10px] text-fg-secondary px-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={asDefault}
                  onChange={(e) => setAsDefault(e.target.checked)}
                  className="accent-accent-info"
                />
                Set as project default (auto-restore on open)
              </label>
            </div>
          ) : (
            <button
              onClick={() => setNaming(true)}
              className="flex items-center gap-1.5 w-full px-2 py-[6px] rounded-md border border-dashed
                border-slate-700 text-[10px] text-fg-secondary hover:text-gray-300 hover:border-slate-500
                transition mt-1"
            >
              <Plus size={11} />
              Save current layers as set
            </button>
          )}
        </div>
      )}
    </div>
  );
}
